import { lazy, Suspense, useMemo, type ReactNode } from "react";
import { createBrowserRouter, RouterProvider } from "react-router";
import { I18nProvider, useI18n } from "./i18n/index.tsx";
import { SessionProvider, useSession } from "./session/SessionProvider.tsx";
import { RealtimeProvider } from "./realtime/RealtimeProvider.tsx";
import { ToastProvider } from "./components/Toast.tsx";
import { LoadingBlock } from "./components/Feedback.tsx";
import { AppShell } from "./shell/AppShell.tsx";
import { ApiUnreachablePage, LoginPage, MfaEnrollmentPage, MfaVerifyPage, SetupPage } from "./pages/AuthPages.tsx";
import { HomePage } from "./pages/HomePage.tsx";
import { ProjectsPage } from "./pages/ProjectsPage.tsx";
import { ProjectPage } from "./pages/project/ProjectPage.tsx";
import { NotFoundPage, PrdsIndexPage } from "./pages/SectionPages.tsx";
import { InboxPage } from "./pages/InboxPage.tsx";

// Wave-3 sections (integrations, memory) load on demand like the other heavy pages.
const IntegrationsPage = lazy(() => import("./pages/IntegrationsPage.tsx").then((m) => ({ default: m.IntegrationsPage })));
const GithubCallbackPage = lazy(() => import("./features/integrations/GithubConnect.tsx").then((m) => ({ default: m.GithubCallbackPage })));
const GithubInstalledPage = lazy(() => import("./features/integrations/GithubConnect.tsx").then((m) => ({ default: m.GithubInstalledPage })));
const MemoryPage = lazy(() => import("./pages/MemoryPage.tsx").then((m) => ({ default: m.MemoryPage })));
// Agents registry, learnings and scores (D20) load on demand.
const AgentsPage = lazy(() => import("./pages/AgentsPage.tsx").then((m) => ({ default: m.AgentsPage })));
const SkillsPage = lazy(() => import("./pages/SkillsPage.tsx").then((m) => ({ default: m.SkillsPage })));

// Configuration, runs and observability are split into their own chunks (loaded on demand).
const SettingsPage = lazy(() => import("./pages/SettingsPage.tsx").then((m) => ({ default: m.SettingsPage })));
const RunsPage = lazy(() => import("./pages/RunsPage.tsx").then((m) => ({ default: m.RunsPage })));
const ObservabilityPage = lazy(() => import("./pages/ObservabilityPage.tsx").then((m) => ({ default: m.ObservabilityPage })));

function Deferred({ children }: { children: ReactNode }) {
  const { t } = useI18n();
  return <Suspense fallback={<LoadingBlock label={t("common.loading")} />}>{children}</Suspense>;
}

function AuthenticatedApp() {
  const router = useMemo(
    () =>
      createBrowserRouter([
        {
          path: "/",
          element: <AppShell />,
          children: [
            { index: true, element: <HomePage /> },
            { path: "chats/:sessionId", element: <HomePage /> },
            { path: "projects", element: <ProjectsPage /> },
            { path: "projects/:projectId/:tab?", element: <ProjectPage /> },
            { path: "runs", element: <Deferred><RunsPage /></Deferred> },
            { path: "prds", element: <PrdsIndexPage /> },
            { path: "memory", element: <Deferred><MemoryPage /></Deferred> },
            { path: "settings/:tab?", element: <Deferred><SettingsPage /></Deferred> },
            { path: "inbox", element: <InboxPage /> },
            { path: "agents", element: <Deferred><AgentsPage /></Deferred> },
            { path: "skills", element: <Deferred><SkillsPage /></Deferred> },
            { path: "integrations", element: <Deferred><IntegrationsPage /></Deferred> },
            { path: "integrations/github/callback", element: <Deferred><GithubCallbackPage /></Deferred> },
            { path: "integrations/github/installed", element: <Deferred><GithubInstalledPage /></Deferred> },
            { path: "observability", element: <Deferred><ObservabilityPage /></Deferred> },
            { path: "*", element: <NotFoundPage /> },
          ],
        },
      ]),
    [],
  );
  return <RouterProvider router={router} />;
}

function Gate() {
  const { status } = useSession();
  const { t } = useI18n();
  const authenticated = status === "authenticated";
  return (
    <RealtimeProvider enabled={authenticated}>
      {status === "loading" ? (
        <main id="main" className="auth">
          <LoadingBlock label={t("common.loading")} />
        </main>
      ) : status === "setup_required" ? (
        <SetupPage />
      ) : status === "signed_out" ? (
        <LoginPage />
      ) : status === "mfa_enrollment" ? (
        <MfaEnrollmentPage />
      ) : status === "mfa_verify" ? (
        <MfaVerifyPage />
      ) : status === "api_unreachable" ? (
        <ApiUnreachablePage />
      ) : (
        <AuthenticatedApp />
      )}
    </RealtimeProvider>
  );
}

export function App() {
  return (
    <I18nProvider>
      <ToastProvider>
        <SessionProvider>
          <Gate />
        </SessionProvider>
      </ToastProvider>
    </I18nProvider>
  );
}
